import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Sparkles, Loader2, Store, ArrowRight } from 'lucide-react'
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { useCreateSubmission, useOnboardingSubmissions } from '@/hooks/onboarding'

export default function OnboardingStart() {
  const navigate = useNavigate()
  const [shopName, setShopName] = useState('')
  const createSubmission = useCreateSubmission()
  const { data: submissions, isLoading: loadingSubmissions } = useOnboardingSubmissions()

  const handleCreate = async () => {
    if (!shopName.trim()) return
    const result = await createSubmission.mutateAsync({ shop_name: shopName.trim() })
    navigate(`/onboarding/${result.id}`)
  }

  const drafts = submissions?.filter((s) => s.status === 'draft') || []

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50">
      <div className="container mx-auto max-w-2xl p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 py-16 text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" />
            AI Fast Lane
          </div>
          <h1 className="text-4xl font-bold">Get Your Barbershop App</h1>
          <p className="mx-auto max-w-lg text-xl text-muted-foreground">
            Enter your shop name to start. We'll set up everything with smart defaults you can
            customize.
          </p>
        </motion.div>

        {/* Create Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-0 bg-card/80 shadow-xl backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Store className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-xl">Start Here</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shopName" className="text-base font-medium">
                  What's your barbershop called?
                </Label>
                <Input
                  id="shopName"
                  placeholder="e.g., Mike's Classic Cuts"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  className="h-12 text-base"
                />
              </div>
              <Button
                size="lg"
                className="h-12 w-full text-base font-medium"
                onClick={handleCreate}
                disabled={!shopName.trim() || createSubmission.isPending}
              >
                {createSubmission.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Setting up...
                  </>
                ) : (
                  <>
                    <ArrowRight className="mr-2 h-5 w-5" /> Create My Barbershop
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Existing Drafts */}
        {!loadingSubmissions && drafts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <h3 className="mb-3 text-lg font-medium">Continue where you left off</h3>
            <div className="space-y-2">
              {drafts.map((sub) => (
                <Card
                  key={sub.id}
                  className="cursor-pointer transition-colors hover:border-primary/50"
                  onClick={() => navigate(`/onboarding/${sub.id}`)}
                >
                  <CardContent className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-medium">{sub.shop_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {sub.barbers_count} barbers · {sub.services_count} services
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div
                          className="h-4 w-4 rounded"
                          style={{ backgroundColor: sub.primary_color }}
                        />
                        <div
                          className="h-4 w-4 rounded"
                          style={{ backgroundColor: sub.secondary_color }}
                        />
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
